import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Show, RedirectToSignIn, useAuth } from "@clerk/react";
import { useFeatureFlagEnabled, usePostHog } from "@posthog/react";

// Wraps react-router's Routes so Sentry attaches a transaction per resolved
// route — without this, every navigation lands in the same anonymous span.
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);
import { PostHogIdentity } from "./components/PostHogIdentity";
import { ReferralAttributionBridge } from "./components/ReferralAttributionBridge";
import { GlobalUpgradeSheet } from "./components/billing/GlobalUpgradeSheet";
import { GlobalTierExplainer } from "./components/verification/global-tier-explainer";
import { SupportProvider } from "./components/support/support-provider";
import { useSessionStartedAnalytics } from "./hooks/useSessionStartedAnalytics";
import { getBrowserPathname, isDevPreviewPath } from "./lib/dev-preview";
import {
  captureReferralAttributionHandoff,
  clearOnboardingResult,
  getAgentReferralFromParams,
  getMarketplaceClaimResumePath,
  getMarketplacePublishResumePath,
  getQuestionSeedFromParams,
  isCompletedOnboardingResult,
  isProRole,
  loadOnboardingSession,
  loadOnboardingResult,
  resolveOnboardingQueryStep,
  saveOnboardingResult,
} from "./lib/onboarding";
import { acceptLegal, fetchCurrentUser } from "./lib/onboarding-api";
import { getProUrl } from "./lib/pro-url";
import type {
  LegalAcceptanceRequirement,
  OnboardingResponse,
} from "./lib/api-types";
import {
  loadFunnelSessionForAccount,
  type FunnelSessionState,
} from "./components/funnel/funnel-session";
import { MFA_REQUIRED_EVENT } from "./lib/api";
import { isAdminApiPath } from "./pages/admin/admin-mfa";
import { captureAuthMfaRequiredShown } from "./lib/auth-analytics";
import {
  INVITED_FUNNEL_ENABLED_FLAG_KEY,
  MARKETPLACE_FUNNEL_ENABLED_FLAG_KEY,
  funnelTestOverrideEnabled,
  invitedFunnelTestOverrideEnabled,
} from "./lib/feature-flags";
import { ADVISOR_REDIRECTS } from "./routes/advisor-redirects";
import {
  isFunnelPosture,
  resolvePosture,
} from "./components/funnel/resolve-posture";
import {
  getOnboardingPathWithUpgradeIntent,
  getUpgradeIntentDestination,
} from "./lib/upgrade-intent";
import { useMessagingEnabled } from "./hooks/useMessagingEnabled";
import { DashboardBadgeSync } from "./components/dashboard/DashboardBadgeSync";
import { DashboardShell } from "./components/dashboard/DashboardShell";
import { DashboardSidebarTransitionProvider } from "./components/dashboard/DashboardSidebarGrid";
import { MarketplaceRouteGate } from "./components/marketplace/MarketplaceRouteGate";
import {
  isMarketplaceRoleSupported,
  marketplaceBrowseHref,
  resolveMarketplaceRole,
} from "./lib/marketplace-route";
import { AnonReadinessRoute } from "./routes/AnonReadinessRoute";
import {
  ConsumerFrame,
  ConsumerFrameChrome,
} from "./routes/ConsumerFrame";
import { ConsumerDocumentTitle } from "./routes/ConsumerDocumentTitle";

// Plan 435 M6 integration pass: `/preview/dashboard` normally renders the
// real, Clerk-backed `DashboardPage` below. With no Clerk key configured
// (the no-key CI posture `playwright.marketplace-ia.config.ts` runs under),
// that would throw — Clerk's `useAuth()` asserts a `ClerkProvider` exists
// regardless of path. Computed once at module scope, same source
// `main.tsx`'s own bypass decision reads.
const CONSUMER_HAS_CLERK_KEY = Boolean(
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
);

const OnboardingPage = lazy(async () => {
  const module = await import("./pages/OnboardingPage");
  return { default: module.OnboardingPage };
});

// M6: Anon AI Chat Door — dedicated /onboarding route with session A/B bucket.
// Separate from OnboardingPage (the authenticated form flow) so the two can
// evolve independently. OnboardingGuard below still uses OnboardingPage for
// the authenticated path.
const AnonOnboardingPage = lazy(async () => {
  const module = await import("./pages/AnonOnboardingPage");
  return { default: module.AnonOnboardingPage };
});

// M3 marketplace onboarding funnel (plan 199) — mounted by OnboardingGuard
// at /onboarding instead of AnonOnboardingPage/OnboardingPage when
// resolvePosture resolves to "marketplace" (organic + flag on) or "invited"
// (referral/invite + invited_funnel_enabled flag on, wake-invited work).
// Referrals otherwise resolve to "legacy" (current onboarding). Legacy is
// the referral + flag-off fallback (D-T1).
const FunnelEngine = lazy(async () => {
  const module = await import("./components/funnel/FunnelEngine");
  return { default: module.FunnelEngine };
});

const DashboardPage = lazy(async () => {
  const module = await import("./pages/DashboardPage");
  return { default: module.DashboardPage };
});

const SettingsPage = lazy(async () => {
  const module = await import("./pages/SettingsPage");
  return { default: module.SettingsPage };
});

// M5-G — Your Offers + minimal consumer agent detail (task:m5-your-offers-page,
// task:m5-agent-detail-page). Gated by the single consumer marketplace launch
// gate (`marketplace_go_live_enabled` via MarketplaceRouteGate — SILL-504).
const OffersPage = lazy(async () => {
  const module = await import("./pages/OffersPage");
  return { default: module.OffersPage };
});

const BrowseAgentsPage = lazy(async () => {
  const module = await import("./pages/BrowseAgentsPage");
  return { default: module.BrowseAgentsPage };
});

const MarketplaceIndexPage = lazy(async () => {
  const module = await import("./pages/MarketplaceIndexPage");
  return { default: module.MarketplaceIndexPage };
});

const OfferDetailPage = lazy(async () => {
  const module = await import("./pages/OfferDetailPage");
  return { default: module.OfferDetailPage };
});

const AgentDetailPage = lazy(async () => {
  const module = await import("./pages/AgentDetailPage");
  return { default: module.AgentDetailPage };
});

// Plan 287 M3 — consumer notification inbox. Mounted inside DashboardShell
// with activeView="feed" so the sidebar bell entry is highlighted.
const FeedPage = lazy(async () => {
  const module = await import("./pages/FeedPage");
  return { default: module.FeedPage };
});

const AgentOfferPage = lazy(async () => {
  const module = await import("./pages/AgentOfferPage");
  return { default: module.AgentOfferPage };
});

// M11-C claim-resume: private, post-signup offer resolution. It stays outside
// the marketplace launch gate because the signed-in owner must be able to
// resolve or safely discard a claim even while ordinary browse is unavailable.
const MarketplaceClaimPage = lazy(async () => {
  const module = await import("./pages/MarketplaceClaimPage");
  return { default: module.MarketplaceClaimPage };
});

// Plan 289 M8-A0 — consumer thread list + thread view (task:consumer-thread-surfaces).
const ThreadsPage = lazy(async () => {
  const module = await import("./pages/ThreadsPage");
  return { default: module.ThreadsPage };
});

const ThreadViewPage = lazy(async () => {
  const module = await import("./pages/ThreadViewPage");
  return { default: module.ThreadViewPage };
});

// Plan 290 M8-A hub bindings (task:hub-stage-container, decision 282/D21) —
// "View profile" route stub. Full relationship profile page is M8-B scope.
const ThreadProfilePage = lazy(async () => {
  const module = await import("./pages/ThreadProfilePage");
  return { default: module.ThreadProfilePage };
});

// Plan 291 M8-B — relationship profile page (task 1395, D21). Mounts inside
// DashboardShell like /feed.
const RelationshipProfilePage = lazy(async () => {
  const module = await import("./pages/RelationshipProfilePage");
  return { default: module.RelationshipProfilePage };
});

// Plan 291 live-QA follow-up (item B) — `/team` connected-pros index, the
// first-class "Your Team" destination. Mounts inside DashboardShell like
// /feed and /team/:proUserId above.
const YourTeamPage = lazy(async () => {
  const module = await import("./pages/YourTeamPage");
  return { default: module.YourTeamPage };
});

const CalculatorsPage = lazy(async () => {
  const module = await import("./pages/CalculatorsPage");
  return { default: module.CalculatorsPage };
});

const TrueCostPage = lazy(async () => {
  const module = await import("./pages/TrueCostPage");
  return { default: module.TrueCostPage };
});

const HelpPage = lazy(async () => {
  const module = await import("./pages/HelpPage");
  return { default: module.HelpPage };
});

// RateAlertsConfigPage retired 2026-05-18 — the rate-alerts editor now
// lives inline on /dashboard/automation. Old deep links redirect there.

const AutomationPage = lazy(async () => {
  const module = await import("./pages/AutomationPage");
  return { default: module.AutomationPage };
});

const PropertyPage = lazy(async () => {
  const module = await import("./pages/PropertyPage");
  return { default: module.PropertyPage };
});

const LoginPage = lazy(async () => {
  const module = await import("./pages/LoginPage");
  return { default: module.LoginPage };
});

const ProJoinPage = lazy(async () => {
  const module = await import("./pages/ProJoinPage");
  return { default: module.ProJoinPage };
});

const InviteLandingPage = lazy(async () => {
  const module = await import("./pages/InviteLandingPage");
  return { default: module.InviteLandingPage };
});

const ProWelcomePage = lazy(async () => {
  const module = await import("./pages/ProWelcomePage");
  return { default: module.ProWelcomePage };
});

// Anonymous readiness page — SILL-398 M7. No auth required; gate is in-page.
const ReadinessPage = lazy(async () => {
  const module = await import("./pages/ReadinessPage");
  return { default: module.ReadinessPage };
});

// AdminSurface is one chunk for the entire /admin/* subtree. Lazy so a
// non-admin browser session never downloads operator UI. Backend
// RequireRole(admin) remains the security boundary regardless.
const AdminSurface = lazy(async () => {
  const module = await import("./pages/admin/AdminSurface");
  return { default: module.AdminSurface };
});

const MobileLayoutPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/MobileLayoutPreviewPage");
      return { default: module.MobileLayoutPreviewPage };
    })
  : null;

const ComponentsPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/ComponentsPreviewPage");
      return { default: module.ComponentsPreviewPage };
    })
  : null;

const VerifiedCopyPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/VerifiedCopyPreviewPage");
      return { default: module.VerifiedCopyPreviewPage };
    })
  : null;

const VerifiedPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/VerifiedPreviewPage");
      return { default: module.VerifiedPreviewPage };
    })
  : null;

const OverviewPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/OverviewPreviewPage");
      return { default: module.OverviewPreviewPage };
    })
  : null;

const PlaidDisconnectPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/PlaidDisconnectPreviewPage");
      return { default: module.PlaidDisconnectPreviewPage };
    })
  : null;

const FunnelPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/FunnelPreviewPage");
      return { default: module.FunnelPreviewPage };
    })
  : null;

const DPAPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/DPAPreviewPage");
      return { default: module.DPAPreviewPage };
    })
  : null;

// M5-H preview enabler (unauthed, no backend) — see
// `pages/OffersPreviewPage.tsx` for the rationale. Distinct from
// `/preview/dashboard`/`/preview/settings` above: those keep Clerk mounted
// (real pages use `useAuth`); these are standalone fixture renders that
// never call `useAuth`, so they work with Clerk fully bypassed.
// M11-B1 report flow + frozen-thread fixture surface (plan 324). DEV-only, so
// it is tree-shaken out of production entirely.
const ReportFlowPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/ReportFlowPreviewPage");
      return { default: module.ReportFlowPreviewPage };
    })
  : null;

const OffersPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/OffersPreviewPage");
      return { default: module.OffersPreviewPage };
    })
  : null;

const MarketplaceClaimPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/MarketplaceClaimPreviewPage");
      return { default: module.MarketplaceClaimPreviewPage };
    })
  : null;

// Plan 435 M2 (mfh-hero-component verification enabler): `HomeHero` across
// the gate matrix + every hero state, no network, no Clerk requirement.
const HomePreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/HomePreviewPage");
      return { default: module.HomePreviewPage };
    })
  : null;

// Plan 435 M6 (asd-fixtures-e2e): Clerk-free `/preview/dashboard` stand-in —
// see `CONSUMER_HAS_CLERK_KEY`'s doc comment above.
const AskSillDashboardPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/AskSillDashboardPreviewPage");
      return { default: module.AskSillDashboardPreviewPage };
    })
  : null;

const BuyerBragPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/BuyerBragPreviewPage");
      return { default: module.BuyerBragPreviewPage };
    })
  : null;

// Plan 289 M8-A0 preview enablers — fixture-only, no fetch/auth (see
// pages/ThreadsPreviewPage.tsx / ThreadViewPreviewPage.tsx).
const ThreadsPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/ThreadsPreviewPage");
      return { default: module.ThreadsPreviewPage };
    })
  : null;

const ThreadViewPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/ThreadViewPreviewPage");
      return { default: module.ThreadViewPreviewPage };
    })
  : null;

// Plan 291 M8-B preview enabler — fixture-only, no fetch/auth (see
// pages/RelationshipProfilePreviewPage.tsx).
const RelationshipProfilePreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/RelationshipProfilePreviewPage");
      return { default: module.RelationshipProfilePreviewPage };
    })
  : null;

// Plan 291 live-QA follow-up (item B) preview enabler — fixture-only, no
// fetch/auth (see pages/TeamPreviewPage.tsx).
const TeamPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/TeamPreviewPage");
      return { default: module.TeamPreviewPage };
    })
  : null;

const BrowseAgentsPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/BrowseAgentsPreviewPage");
      return { default: module.BrowseAgentsPreviewPage };
    })
  : null;

const OfferDetailPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/OfferDetailPreviewPage");
      return { default: module.OfferDetailPreviewPage };
    })
  : null;

const AgentDetailPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/AgentDetailPreviewPage");
      return { default: module.AgentDetailPreviewPage };
    })
  : null;

const AgentOfferPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/AgentOfferPreviewPage");
      return { default: module.AgentOfferPreviewPage };
    })
  : null;

// M3 preview enabler — every MarketplaceStatusCard state in both placements.
// Unauthed, no backend. Gated behind import.meta.env.DEV.
const MarketplaceStatusPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/MarketplaceStatusPreviewPage");
      return { default: module.MarketplaceStatusPreviewPage };
    })
  : null;

// M11-C2 preview enabler — the geo-waitlist position card (both states).
// Unauthed, no backend. Gated behind import.meta.env.DEV.
const WaitlistPositionPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/WaitlistPositionPreviewPage");
      return { default: module.WaitlistPositionPreviewPage };
    })
  : null;

// M4 preview enabler — both AcceptConsentSheet variants in all submit states
// + the accept CTA chrome pattern. Unauthed, no backend.
const AcceptConsentSheetPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/AcceptConsentSheetPreviewPage");
      return { default: module.AcceptConsentSheetPreviewPage };
    })
  : null;

// M5 preview enabler — DisconnectMarketplaceDialog all three steps + done state.
// Unauthed, no backend. Gated behind import.meta.env.DEV.
const DisconnectMarketplaceDialogPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module =
        await import("./pages/DisconnectMarketplaceDialogPreviewPage");
      return { default: module.DisconnectMarketplaceDialogPreviewPage };
    })
  : null;

// M6 preview enabler — sidebar Marketplace nav group (badge 0/n, Browse agents disabled).
// Renders ONLY the Marketplace group via SidebarStepperButton — no Clerk, no SidebarPlusBadgeButton.
const MarketplaceSidebarPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/MarketplaceSidebarPreviewPage");
      return { default: module.MarketplaceSidebarPreviewPage };
    })
  : null;

// M6 preview enabler — all four PassportPublishBlock states (connected/live/not-published/concierge).
// Exports PassportPublishBlock from profile-view.tsx; no Clerk, no backend.
const PassportPublishPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/PassportPublishPreviewPage");
      return { default: module.PassportPublishPreviewPage };
    })
  : null;

// Plan 327 preview enabler — production Admin Pros marketing-teaser control.
const AdminMarketingTeaserPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/AdminMarketingTeaserPreviewPage");
      return { default: module.AdminMarketingTeaserPreviewPage };
    })
  : null;

// Plan 287 M3 preview enablers — Feed inbox + Settings notifications.
// Unauthed, no backend. Gated behind import.meta.env.DEV.
const FeedPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/FeedPreviewPage");
      return { default: module.FeedPreviewPage };
    })
  : null;

const SettingsNotificationsPreviewPage = import.meta.env.DEV
  ? lazy(async () => {
      const module = await import("./pages/SettingsNotificationsPreviewPage");
      return { default: module.SettingsNotificationsPreviewPage };
    })
  : null;

/**
 * Redirects to /dashboard only when the user arrived already signed in AND
 * has a backend record. Two cases route to onboarding instead:
 *
 * 1. Not signed in — normal first-time visitor.
 * 2. Signed in but fetchCurrentUser returns 404 — Clerk account exists but the
 *    backend submission never completed (e.g. tab closed between OTP
 *    verification and the API response). OnboardingFlow's handleSignUpAndSubmit
 *    detects isSignedIn and skips sign-up, going straight to submitToBackend
 *    with the saved session data.
 *
 * initiallySignedIn is snapshotted at render time (not in an effect) so
 * mid-flow auth changes — signUp.finalize() setting isSignedIn = true — never
 * re-trigger this guard while a submission is in flight.
 */
function OnboardingGuard() {
  const { isSignedIn, isLoaded, getToken } = useAuth();
  const location = useLocation();
  const [initiallySignedIn, setInitiallySignedIn] = useState<boolean | null>(
    null,
  );
  const [backendOnboardingComplete, setBackendOnboardingComplete] = useState<
    boolean | null
  >(null);
  const [funnelResume, setFunnelResume] = useState<{
    session: FunnelSessionState;
    result: OnboardingResponse;
  } | null>(null);
  const deepLinkedStep =
    typeof window === "undefined"
      ? null
      : resolveOnboardingQueryStep(new URLSearchParams(window.location.search));

  // M3: posture resolution (resolvePosture) decides whether the funnel
  // engine mounts instead of the legacy AnonOnboardingPage/OnboardingPage
  // split. Referral/invite params resolve to "invited" when
  // `invited_funnel_enabled` is on, else "legacy" (current onboarding); for
  // non-referred visitors the `marketplace_funnel_enabled` flag governs
  // "marketplace" (funnel) vs "legacy".
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  useEffect(() => {
    captureReferralAttributionHandoff(searchParams);
  }, [searchParams]);
  const referral = useMemo(
    () => getAgentReferralFromParams(searchParams),
    [searchParams],
  );
  // The Ask Sill seed. Not a posture input (resolvePosture is deliberately
  // blind to it) — it decides whether the seed-answer entry renders in FRONT
  // of the funnel.
  const seedQuestion = useMemo(
    () => getQuestionSeedFromParams(searchParams),
    [searchParams],
  );
  const funnelFlagEnabled = useFeatureFlagEnabled(
    MARKETPLACE_FUNNEL_ENABLED_FLAG_KEY,
  );
  const invitedFunnelFlagEnabled = useFeatureFlagEnabled(
    INVITED_FUNNEL_ENABLED_FLAG_KEY,
  );
  // The PostHog flag is the production switch; the test override
  // (VITE_MARKETPLACE_FUNNEL_ENABLED / DEV `?funnel=1`) exists so the typical
  // path is walkable in local + staging where that flag often doesn't
  // resolve. `funnelEnabled` only affects organic visitors (resolvePosture
  // never consults it for a referral). invitedFunnelFlagEnabled /
  // invitedFunnelTestOverrideEnabled mirror that pair for referral/invite
  // traffic instead — resolvePosture consults them independently of
  // funnelEnabled (see its doc comment).
  const posture = resolvePosture(searchParams, referral, {
    funnelEnabled:
      funnelFlagEnabled === true ||
      funnelTestOverrideEnabled(searchParams, import.meta.env),
    invitedFunnelEnabled:
      invitedFunnelFlagEnabled === true ||
      invitedFunnelTestOverrideEnabled(searchParams, import.meta.env),
  });

  // Latch the funnel posture the first time it resolves (item 4 — no
  // post-code login flash). After the funnel's account step calls
  // signUp.finalize(), isSignedIn flips true and PostHogIdentity re-identifies
  // the user, which makes useFeatureFlagEnabled briefly re-resolve
  // (funnelFlagEnabled flickers to undefined). Without latching, posture would
  // flip marketplace→legacy for a beat — tearing down <FunnelEngine> and
  // flashing <AnonOnboardingPage> before remounting the funnel. Snapshotting
  // the funnel posture keeps it mounted continuously through the whole
  // sign-in handshake. Same set-state-during-render idiom as
  // initiallySignedIn below; only latches a FUNNEL posture (legacy stays
  // dynamic so a slow first-load flag resolution still upgrades to the funnel).
  const [latchedFunnelPosture, setLatchedFunnelPosture] = useState<ReturnType<
    typeof resolvePosture
  > | null>(null);
  if (latchedFunnelPosture === null && isFunnelPosture(posture)) {
    setLatchedFunnelPosture(posture);
  }
  const effectivePosture = latchedFunnelPosture ?? posture;

  // Capture auth state the first time Clerk finishes loading.
  // Setting state during render is intentional — React re-renders immediately
  // with the captured value, avoiding an effect + extra paint.
  if (isLoaded && initiallySignedIn === null) {
    setInitiallySignedIn(!!isSignedIn);
  }

  useEffect(() => {
    // Only runs when the user arrived already signed in and the check hasn't
    // resolved yet. setState calls are in async callbacks, not the effect body.
    if (initiallySignedIn !== true || backendOnboardingComplete !== null)
      return;

    getToken()
      .then((token) => (token ? fetchCurrentUser(token) : Promise.reject()))
      .then((result) => {
        if (isCompletedOnboardingResult(result)) {
          const session = loadFunnelSessionForAccount(result.user.id);
          if (session) setFunnelResume({ session, result });
          saveOnboardingResult(result);
          setBackendOnboardingComplete(true);
          return;
        }

        // A signed-in account with no buyer profile that is actually a pro
        // (agent/LO) belongs in the CRM, not buyer onboarding. Hard-redirect
        // to the pro app (separate origin) and leave the guard pending so we
        // never flash the onboarding form. Leaving backendOnboardingComplete
        // null keeps render() returning null until navigation commits.
        if (isProRole(result)) {
          clearOnboardingResult();
          window.location.href = getProUrl("/");
          return;
        }

        clearOnboardingResult();
        setBackendOnboardingComplete(false);
      })
      .catch(() => setBackendOnboardingComplete(false));
  }, [initiallySignedIn, backendOnboardingComplete, getToken]);

  if (!isLoaded || initiallySignedIn === null) return null;

  // Not signed in at mount. Funnel-on postures (marketplace/invited) mount
  // the M3 funnel engine and never touch AnonOnboardingPage — this is what
  // "pauses the chat-door A/B" (tech spec §"Frontend"): the door experiment
  // lives entirely inside AnonOnboardingPage, so bypassing it suppresses the
  // experiment rather than needing a second kill switch.
  if (!initiallySignedIn) {
    if (isFunnelPosture(effectivePosture)) {
      // An Ask Sill seed answers FIRST, then hands off into the funnel. The
      // marketing landing promises an answer to what the visitor typed, and
      // the funnel has no question-seeded screen — so the seed-answer entry
      // renders in front of it. `AnonOnboardingPage` owns that entry (anon
      // session + the `onboarding_door` chat/form bucket); passing
      // `funnelPosture` is what makes its bridge land in the funnel instead of
      // the legacy form. With the door flag off — the default — every seeded
      // visitor takes the form arm, so this is: answer once, then funnel.
      if (seedQuestion) {
        return (
          <RouteFallback>
            <AnonOnboardingPage funnelPosture={effectivePosture} />
          </RouteFallback>
        );
      }
      return (
        <RouteFallback>
          <FunnelEngine posture={effectivePosture} />
        </RouteFallback>
      );
    }

    // M6 AI Chat Door. AnonOnboardingPage creates an anon session and
    // A/B-buckets into chat or form. ?form=1 always shows the form; ?q=
    // carries the seed question from the landing band.
    return (
      <RouteFallback>
        <AnonOnboardingPage />
      </RouteFallback>
    );
  }

  // Was signed in at mount — wait for the backend check to resolve.
  if (backendOnboardingComplete === null) return null;
  if (funnelResume) {
    return (
      <RouteFallback>
        <FunnelEngine
          posture={funnelResume.session.posture}
          resumeSession={funnelResume.session}
          resumeResult={funnelResume.result}
        />
      </RouteFallback>
    );
  }
  if (backendOnboardingComplete) {
    const session = loadOnboardingSession();
    const claimResumePath = getMarketplaceClaimResumePath(
      session?.marketplaceClaimToken,
    );
    const marketplacePublishPath = getMarketplacePublishResumePath(
      session?.marketplacePublishIntent,
    );
    return (
      <Navigate
        replace
        to={
          claimResumePath ??
          marketplacePublishPath ??
          getUpgradeIntentDestination(location.search) ??
          "/dashboard"
        }
      />
    );
  }

  if (isFunnelPosture(effectivePosture)) {
    // Same seed-answers-first rule as the signed-out branch above.
    if (seedQuestion) {
      return (
        <RouteFallback>
          <AnonOnboardingPage funnelPosture={effectivePosture} />
        </RouteFallback>
      );
    }
    return (
      <RouteFallback>
        <FunnelEngine posture={effectivePosture} />
      </RouteFallback>
    );
  }

  if (deepLinkedStep !== null) {
    return (
      <RouteFallback>
        <OnboardingPage />
      </RouteFallback>
    );
  }

  return (
    <RouteFallback>
      <OnboardingPage />
    </RouteFallback>
  );
}

function RequireCompletedOnboarding({
  children,
}: {
  children: React.ReactNode;
}) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState<"checking" | "complete" | "incomplete">(
    () => (loadOnboardingResult() ? "complete" : "checking"),
  );

  useEffect(() => {
    if (!isLoaded || !isSignedIn || status !== "checking") {
      return;
    }

    let cancelled = false;

    getToken()
      .then((token) => (token ? fetchCurrentUser(token) : Promise.reject()))
      .then((result) => {
        if (cancelled) {
          return;
        }

        if (isCompletedOnboardingResult(result)) {
          saveOnboardingResult(result);
          setStatus("complete");
          return;
        }

        clearOnboardingResult();
        setStatus("incomplete");
      })
      .catch(() => {
        // If we can't confirm onboarding completion, route to /onboarding,
        // not /dashboard. The dashboard assumes a backend profile exists
        // and will cascade 401/404s when it doesn't. /onboarding handles
        // both the "fresh Clerk user, no profile yet" case and a stale
        // session — the user can resume or re-submit. Defaulting to
        // "complete" here previously stranded users on a broken dashboard.
        if (!cancelled) {
          clearOnboardingResult();
          setStatus("incomplete");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [getToken, isLoaded, isSignedIn, status]);

  if (!isLoaded || !isSignedIn || status === "checking") {
    return null;
  }

  if (status === "incomplete") {
    return (
      <Navigate
        replace
        to={getOnboardingPathWithUpgradeIntent(location.search)}
      />
    );
  }

  return <ReferralAttributionBridge>{children}</ReferralAttributionBridge>;
}

function LegalAcceptanceGate({ children }: { children: React.ReactNode }) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [status, setStatus] = useState<
    "checking" | "clear" | "required" | "error"
  >("checking");
  const [legal, setLegal] = useState<LegalAcceptanceRequirement | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || status !== "checking") {
      return;
    }
    let cancelled = false;

    getToken()
      .then((token) => (token ? fetchCurrentUser(token) : Promise.reject()))
      .then((result) => {
        if (cancelled) return;
        if (result.requires_legal_acceptance && result.legal) {
          setLegal(result.legal);
          setStatus("required");
          return;
        }
        setStatus("clear");
      })
      .catch(() => {
        if (!cancelled) {
          setError("Could not verify current terms. Try again.");
          setStatus("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [getToken, isLoaded, isSignedIn, status]);

  function retryLegalCheck() {
    setError(null);
    setStatus("checking");
  }

  async function handleAccept() {
    if (!legal) return;
    setAccepting(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("Missing session token");
      const result = await acceptLegal(
        {
          tos_version: legal.tos.version,
          privacy_version: legal.privacy.version,
        },
        token,
      );
      if (result.requires_legal_acceptance) {
        setError("Legal documents changed. Refresh and try again.");
        return;
      }
      setStatus("clear");
    } catch {
      setError("Could not save acceptance. Try again.");
    } finally {
      setAccepting(false);
    }
  }

  if (!isLoaded || !isSignedIn || status === "checking") {
    return null;
  }
  if (status === "clear") {
    return <>{children}</>;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 px-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-acceptance-title"
        className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-xl"
      >
        <h1
          id="legal-acceptance-title"
          className="text-xl font-semibold text-foreground"
        >
          Review Sill terms
        </h1>
        {status === "required" ? (
          <>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              Accept the current Terms of Service and Privacy Policy to
              continue.
            </p>
            <div className="mt-5 grid gap-3 text-sm">
              <a
                className="text-primary underline-offset-4 hover:underline"
                href={legal?.tos.url}
                rel="noreferrer"
                target="_blank"
              >
                Terms of Service
              </a>
              <a
                className="text-primary underline-offset-4 hover:underline"
                href={legal?.privacy.url}
                rel="noreferrer"
                target="_blank"
              >
                Privacy Policy
              </a>
            </div>
          </>
        ) : null}
        {error ? (
          <p className="mt-4 text-sm text-destructive">{error}</p>
        ) : null}
        {status === "required" ? (
          <button
            className="mt-6 inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={accepting}
            onClick={handleAccept}
            type="button"
          >
            {accepting ? "Saving..." : "Accept and continue"}
          </button>
        ) : (
          <button
            className="mt-6 inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            onClick={retryLegalCheck}
            type="button"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

function SessionStartedAnalytics() {
  useSessionStartedAnalytics();
  return null;
}

export function MFARequiredDialog() {
  const [open, setOpen] = useState(false);
  const posthog = usePostHog();

  useEffect(() => {
    const onRequired = (event: Event) => {
      // Admin calls are handled in place by AdminMFAGate (SEC-02), which
      // opens Clerk's reverification prompt instead of this fallback.
      const path = (event as CustomEvent<{ path?: string }>).detail?.path;
      if (isAdminApiPath(path)) return;
      setOpen(true);
    };
    window.addEventListener(MFA_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(MFA_REQUIRED_EVENT, onRequired);
  }, []);

  // Once per open, never per render. This fallback tells the user to retry or
  // sign back in with MFA they already have, so the remedy is `reverify`.
  useEffect(() => {
    if (open) {
      captureAuthMfaRequiredShown(
        (event, props) => posthog?.capture?.(event, props),
        "reverify",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-background/80 px-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mfa-required-title"
        className="max-h-[90vh] w-full max-w-xl overflow-auto rounded-lg border border-border bg-card p-5 shadow-xl"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h1
              id="mfa-required-title"
              className="text-lg font-semibold text-foreground"
            >
              Secure your account
            </h1>
            <p className="text-sm leading-6 text-muted-foreground">
              This protected action needs a recent MFA check. Close this message
              and try the action again; if you keep seeing it, sign out and sign
              back in with MFA.
            </p>
          </div>
          <button
            className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-md border border-border px-3 text-sm font-medium text-foreground transition-[background-color,color,border-color] hover:bg-muted"
            onClick={() => setOpen(false)}
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductRoute({ children }: { children: React.ReactNode }) {
  return (
    <Show when="signed-in" fallback={<RedirectToSignIn />}>
      <RequireCompletedOnboarding>
        <LegalAcceptanceGate>
          <RouteFallback>{children}</RouteFallback>
        </LegalAcceptanceGate>
      </RequireCompletedOnboarding>
    </Show>
  );
}

// SILL-504 — every consumer marketplace route stacks MarketplaceRouteGate
// (components/marketplace/MarketplaceRouteGate.tsx) INSIDE its outermost
// ProductRoute; see that file for the flag + reconciliation contract.
// BrowseRoute adds only the browse-specific role-param validation on top.

// Plan 289 M8-A0 — gates `/threads` and `/threads/:id` on `messaging_enabled`
// (D19). Mounts INSIDE the outer ProductRoute so auth/onboarding/legal gates
// aren't remounted on navigation (same idiom as MarketplaceRouteGate).
function MessagingFlagGate({ children }: { children: React.ReactNode }) {
  const messagingEnabled = useMessagingEnabled();
  if (!messagingEnabled) {
    return <Navigate replace to="/dashboard" />;
  }
  return <>{children}</>;
}

// Captures an opaque referral handoff (`?referral_token=`, marketplace claim,
// publish intent) from ANY entry route — not just /onboarding. The Settings
// share link lands a friend on the marketing site, whose Log In link forwards
// `referral_token` to /login; a friend who already has an account signs in
// there, lands on /dashboard, and ReferralAttributionBridge attributes from the
// captured session field post-auth. Before this, only OnboardingGuard captured,
// so the existing-account path silently attributed nothing. Idempotent with the
// guard's own capture (same token, same session field).
function ReferralHandoffCapture() {
  const location = useLocation();
  useEffect(() => {
    captureReferralAttributionHandoff(new URLSearchParams(location.search));
  }, [location.search]);
  return null;
}

function BrowseRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const role = resolveMarketplaceRole(new URLSearchParams(location.search));
  if (!isMarketplaceRoleSupported(role)) {
    return <Navigate replace to={marketplaceBrowseHref("agent")} />;
  }
  return <MarketplaceRouteGate>{children}</MarketplaceRouteGate>;
}

export function App() {
  const shouldBypassClerk = isDevPreviewPath(getBrowserPathname());

  return (
    <>
      <ConsumerDocumentTitle />
      {shouldBypassClerk ? null : <PostHogIdentity />}
      {shouldBypassClerk ? null : <SessionStartedAnalytics />}
      {shouldBypassClerk ? null : <DashboardBadgeSync />}
      <ReferralHandoffCapture />
      <GlobalTierExplainer />
      <MFARequiredDialog />
      {/* App-level subscriber for the http-paywall-interceptor event
          bus. Any 403 feature_not_available response from apiRequest
          opens this contextual UpgradeSheet — even from call sites that
          don't own their own paywall. See GlobalUpgradeSheet.tsx for
          the per-call-site interaction with locally-mounted sheets. */}
      <GlobalUpgradeSheet />
      <SupportProvider>
        <DashboardSidebarTransitionProvider>
          <SentryRoutes>
            <Route path="/" element={<OnboardingGuard />} />
            <Route path="/onboarding" element={<OnboardingGuard />} />
            <Route path="/assessment" element={<OnboardingGuard />} />

            {/* Anonymous readiness page — SILL-398 M7. Public; gate is in-page.
                Unlinked since the marketplace funnel launch (kept as a
                possible future onboarding avenue); a visitor already signed
                in on arrival is redirected to /dashboard?view=readiness.
                No ProductRoute / auth guard: anons arrive here after onboarding
                intake (M6). Pattern B gate fires on arrival. */}
            <Route
              path="/readiness"
              element={
                <AnonReadinessRoute>
                  <RouteFallback>
                    <ReadinessPage />
                  </RouteFallback>
                </AnonReadinessRoute>
              }
            />

            <Route
              path="/login/*"
              element={
                <RouteFallback>
                  <LoginPage />
                </RouteFallback>
              }
            />

            <Route
              path="/pros/join"
              element={
                <RouteFallback>
                  <ProJoinPage />
                </RouteFallback>
              }
            />

            {/* CRM "Invite to Sill" email landing: resolves the per-invite
                token to the agent's code, then hands off to onboarding. */}
            <Route
              path="/invite/:token"
              element={
                <RouteFallback>
                  <InviteLandingPage />
                </RouteFallback>
              }
            />

            <Route
              path="/pros/welcome"
              element={
                <RouteFallback>
                  <ProWelcomePage />
                </RouteFallback>
              }
            />

            {/* Plan 435 M6 integration pass (task asd-consumer-frame): the
                ONE `ConsumerFrame` layout route wrapping every authed
                consumer route in this group — it mounts the shared
                `AdvisorSessionProvider` + `AdvisorDrawer` + the end-turn
                readiness watcher ONCE, so the Ask Sill drawer and its
                conversation survive navigation between every child below. */}
            <Route element={<ConsumerFrame />}>
            <Route
              path="/dashboard"
              element={
                <ProductRoute>
                  <DashboardPage />
                </ProductRoute>
              }
            />

            <Route
              path="/dashboard/settings"
              element={
                <ProductRoute>
                  <SettingsPage />
                </ProductRoute>
              }
            />

            {/* Automation surface — Plus-only "things Sill does for you
                on a schedule" (rate alerts today, future siblings later).
                The overview is reachable for free + Plus; per-automation
                drill-downs gate at their own page boundary. */}
            <Route
              path="/dashboard/automation"
              element={
                <ProductRoute>
                  <AutomationPage />
                </ProductRoute>
              }
            />
            {/* Both old rate-alerts deep links now redirect into the
                automation overview, where the editor lives inline. The
                standalone RateAlertsConfigPage was retired 2026-05-18 —
                see commit message. Replace = true so Back doesn't
                ping-pong through the redirect. */}
            <Route
              path="/dashboard/automation/rate-alerts"
              element={<Navigate replace to="/dashboard/automation" />}
            />
            <Route
              path="/dashboard/rate-alerts"
              element={<Navigate replace to="/dashboard/automation" />}
            />

            <Route
              path="/dashboard/passport"
              element={
                <ProductRoute>
                  <Navigate replace to="/dashboard?view=passport" />
                </ProductRoute>
              }
            />

            <Route
              path="/dashboard/publish"
              element={<Navigate replace to="/dashboard?view=passport" />}
            />

            {/* Marketplace pages share ONE persistent shell: DashboardShell +
                sidebar + header mount once as the layout, and only the
                <Outlet/> content swaps on navigation between them (no full
                remount / "page refresh"). */}
            <Route
              element={
                <ProductRoute>
                  <DashboardShell>
                    <Outlet />
                  </DashboardShell>
                </ProductRoute>
              }
            >
              <Route
                path="/offers"
                element={
                  <MarketplaceRouteGate>
                    <OffersPage />
                  </MarketplaceRouteGate>
                }
              />
              <Route
                path="/offers/:id"
                element={
                  <MarketplaceRouteGate>
                    <OfferDetailPage />
                  </MarketplaceRouteGate>
                }
              />
              <Route
                path="/market/agents/:id"
                element={
                  <MarketplaceRouteGate>
                    <AgentDetailPage />
                  </MarketplaceRouteGate>
                }
              />
              <Route
                path="/market/agents/:id/offers/:ref"
                element={
                  <MarketplaceRouteGate>
                    <AgentOfferPage />
                  </MarketplaceRouteGate>
                }
              />
              {/* Plan 289 M8-A0 — consumer thread surfaces (CARRY-3: the
                  backend notify email deep-links to /threads/:id verbatim). */}
              <Route
                path="/threads"
                element={
                  <MessagingFlagGate>
                    <ThreadsPage />
                  </MessagingFlagGate>
                }
              />
              <Route
                path="/threads/:id"
                element={
                  <MessagingFlagGate>
                    <ThreadViewPage />
                  </MessagingFlagGate>
                }
              />
              {/* Plan 290 M8-A hub bindings — "View profile" header button
                  destination (decision 282/D21). Route stub; full page is
                  M8-B scope. Rewiring this stub to RelationshipProfilePage
                  is plan 290's follow-up job (RelationshipProfilePage's own
                  docstring), not part of this task's authorized scope. */}
              <Route
                path="/threads/:id/profile"
                element={
                  <MessagingFlagGate>
                    <ThreadProfilePage />
                  </MessagingFlagGate>
                }
              />
              <Route
                path="/marketplace"
                element={
                  <BrowseRoute>
                    <MarketplaceIndexPage />
                  </BrowseRoute>
                }
              />
              <Route
                path="/marketplace/browse"
                element={
                  <BrowseRoute>
                    <BrowseAgentsPage />
                  </BrowseRoute>
                }
              />
              <Route
                path="/marketplace/claims/:token"
                element={<MarketplaceClaimPage />}
              />
            </Route>

            {/* Plan 287 M3 — consumer notification inbox.
                Uses DashboardShell (sidebar + header) so the bell entry
                is highlighted and the readiness score is accessible.
                No PostHog flag gate: Feed renders unconditionally. */}
            <Route
              path="/feed"
              element={
                <ProductRoute>
                  <DashboardShell activeView="feed">
                    <FeedPage />
                  </DashboardShell>
                </ProductRoute>
              }
            />

            {/* Plan 291 live-QA follow-up (item B) — the "Your Team" index.
                Deliberately NOT wrapped in MarketplaceRouteGate: checked
                against /team/:proUserId below, which is also OUTSIDE the
                MarketplaceRouteGate-wrapped marketplace routes — an invited
                (non-marketplace) team relationship must stay reachable while
                the SILL-504 launch gate is closed, so this index matches the
                least-restrictive-correct choice the detail page already
                makes. */}
            <Route
              path="/team"
              element={
                <ProductRoute>
                  <DashboardShell activeView="team">
                    <YourTeamPage />
                  </DashboardShell>
                </ProductRoute>
              }
            />

            {/* Plan 291 M8-B — relationship profile page (task 1395, D21).
                Mounts inside DashboardShell like /feed and /team above;
                direct-URL reachable regardless of the thread-header "View
                profile" wiring (plan 290's job). Deliberately given its OWN
                DashboardShell instance with an explicit `activeView="team"`
                (mirroring /team, not the shared marketplace-group layout
                Route) — the layout Route's DashboardShell has no per-child
                activeView, so it fell back to `marketplaceShellDestination`,
                which defaults to "offers" for any unmatched path. That made
                the sidebar highlight BOTH "Your Offers" (from the
                mis-defaulted activeView) AND "Your Team" (which
                independently self-highlights via isDashboardPathActive's
                /team/ prefix match) at the same time — operator screenshot
                fix. */}
            <Route
              path="/team/:proUserId"
              element={
                <ProductRoute>
                  <DashboardShell activeView="team">
                    <RelationshipProfilePage />
                  </DashboardShell>
                </ProductRoute>
              }
            />

            <Route
              path="/properties/:propertyId"
              element={
                <ProductRoute>
                  <PropertyPage />
                </ProductRoute>
              }
            />

            <Route
              path="/calculators"
              element={
                <ProductRoute>
                  <CalculatorsPage />
                </ProductRoute>
              }
            />
            <Route
              path="/calculators/true-cost"
              element={
                <ProductRoute>
                  <TrueCostPage />
                </ProductRoute>
              }
            />
            <Route
              path="/help"
              element={
                <ProductRoute>
                  <HelpPage />
                </ProductRoute>
              }
            />
            <Route
              path="/calculators/:tool"
              element={
                <ProductRoute>
                  <CalculatorsPage />
                </ProductRoute>
              }
            />
            </Route>
            {import.meta.env.DEV && ComponentsPreviewPage ? (
              <>
                <Route
                  path="/preview/dashboard"
                  element={
                    <RouteFallback>
                      <ConsumerFrameChrome>
                        {/* Plan 435 M6 (asd-fixtures-e2e): a real Clerk key
                            keeps this route's pre-existing behavior
                            (the real DashboardPage, for local dev preview
                            of the authed experience); with none — the
                            no-key CI posture — it swaps to the Clerk-free
                            fixture `ask-sill-drawer.spec.ts` drives. */}
                        {CONSUMER_HAS_CLERK_KEY || !AskSillDashboardPreviewPage ? (
                          <DashboardPage />
                        ) : (
                          <AskSillDashboardPreviewPage />
                        )}
                      </ConsumerFrameChrome>
                    </RouteFallback>
                  }
                />
                <Route
                  path="/preview/settings"
                  element={
                    <RouteFallback>
                      <SettingsPage />
                    </RouteFallback>
                  }
                />
                <Route
                  path="/__components"
                  element={
                    <RouteFallback>
                      <ComponentsPreviewPage />
                    </RouteFallback>
                  }
                />
                {MobileLayoutPreviewPage ? (
                  <Route
                    path="/preview/mobile-layout"
                    element={<RouteFallback><MobileLayoutPreviewPage /></RouteFallback>}
                  />
                ) : null}
                {VerifiedCopyPreviewPage ? (
                  <Route
                    path="/__verified-copy"
                    element={
                      <RouteFallback>
                        <VerifiedCopyPreviewPage />
                      </RouteFallback>
                    }
                  />
                ) : null}
                {VerifiedPreviewPage ? (
                  <Route
                    path="/__verified"
                    element={
                      <RouteFallback>
                        <VerifiedPreviewPage />
                      </RouteFallback>
                    }
                  />
                ) : null}
                {OverviewPreviewPage ? (
                  <Route
                    path="/__overview"
                    element={
                      <RouteFallback>
                        <OverviewPreviewPage />
                      </RouteFallback>
                    }
                  />
                ) : null}
                {PlaidDisconnectPreviewPage ? (
                  <Route
                    path="/preview/plaid-disconnect"
                    element={
                      <RouteFallback>
                        <PlaidDisconnectPreviewPage />
                      </RouteFallback>
                    }
                  />
                ) : null}
                {FunnelPreviewPage ? (
                  <Route
                    path="/preview/funnel"
                    element={
                      <RouteFallback>
                        <FunnelPreviewPage />
                      </RouteFallback>
                    }
                  />
                ) : null}
                {DPAPreviewPage ? (
                  <Route
                    path="/__dpa"
                    element={
                      <RouteFallback>
                        <DPAPreviewPage />
                      </RouteFallback>
                    }
                  />
                ) : null}
                {OffersPreviewPage ? (
                  <Route
                    path="/preview/offers"
                    element={
                      <RouteFallback>
                        {/* Plan 435 M6 (asd-fixtures-e2e): `?ask=1` opens
                            the Clerk-free drawer fixture here too — see
                            `ConsumerFrameChrome`'s doc comment. */}
                        <ConsumerFrameChrome>
                          <DashboardShell>
                            <OffersPreviewPage />
                          </DashboardShell>
                        </ConsumerFrameChrome>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {MarketplaceClaimPreviewPage ? (
                  <Route
                    path="/preview/marketplace/claim"
                    element={
                      <RouteFallback>
                        <DashboardShell>
                          <MarketplaceClaimPreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {HomePreviewPage ? (
                  <Route
                    path="/preview/home"
                    element={
                      <RouteFallback>
                        <DashboardShell>
                          <HomePreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {BuyerBragPreviewPage ? (
                  <Route
                    path="/preview/marketplace/buyer-brag"
                    element={
                      <RouteFallback>
                        <BuyerBragPreviewPage />
                      </RouteFallback>
                    }
                  />
                ) : null}
                {ReportFlowPreviewPage ? (
                  <Route
                    path="/preview/report-flow"
                    element={
                      <RouteFallback>
                        <ReportFlowPreviewPage />
                      </RouteFallback>
                    }
                  />
                ) : null}
                {BrowseAgentsPreviewPage ? (
                  <Route
                    path="/preview/marketplace/browse"
                    element={
                      <RouteFallback>
                        <ConsumerFrameChrome>
                          <DashboardShell>
                            <BrowseAgentsPreviewPage />
                          </DashboardShell>
                        </ConsumerFrameChrome>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {OfferDetailPreviewPage ? (
                  <Route
                    path="/preview/offers/:id"
                    element={
                      <RouteFallback>
                        <DashboardShell>
                          <OfferDetailPreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {AgentDetailPreviewPage ? (
                  <Route
                    path="/preview/market/agents/:id"
                    element={
                      <RouteFallback>
                        <DashboardShell>
                          <AgentDetailPreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {AgentOfferPreviewPage ? (
                  <Route
                    path="/preview/market/agents/:id/offers/:ref"
                    element={
                      <RouteFallback>
                        <DashboardShell>
                          <AgentOfferPreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {MarketplaceStatusPreviewPage ? (
                  <Route
                    path="/preview/marketplace/status-card"
                    element={
                      <RouteFallback>
                        <DashboardShell>
                          <MarketplaceStatusPreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {WaitlistPositionPreviewPage ? (
                  <Route
                    path="/preview/marketplace/waitlist"
                    element={
                      <RouteFallback>
                        <DashboardShell>
                          <WaitlistPositionPreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {AcceptConsentSheetPreviewPage ? (
                  <Route
                    path="/preview/marketplace/consent-sheet"
                    element={
                      <RouteFallback>
                        <DashboardShell>
                          <AcceptConsentSheetPreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {DisconnectMarketplaceDialogPreviewPage ? (
                  <Route
                    path="/preview/marketplace/disconnect-dialog"
                    element={
                      <RouteFallback>
                        <DashboardShell>
                          <DisconnectMarketplaceDialogPreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {/* M6 preview routes — sidebar nav group + passport publish states */}
                {MarketplaceSidebarPreviewPage ? (
                  <Route
                    path="/preview/marketplace/sidebar-nav"
                    element={
                      <RouteFallback>
                        <MarketplaceSidebarPreviewPage />
                      </RouteFallback>
                    }
                  />
                ) : null}
                {PassportPublishPreviewPage ? (
                  <Route
                    path="/preview/marketplace/passport-publish"
                    element={
                      <RouteFallback>
                        <DashboardShell>
                          <PassportPublishPreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {AdminMarketingTeaserPreviewPage ? (
                  <Route
                    path="/preview/admin/marketing-teaser"
                    element={
                      <RouteFallback>
                        <AdminMarketingTeaserPreviewPage />
                      </RouteFallback>
                    }
                  />
                ) : null}
                {/* Plan 289 M8-A0 — thread list + thread view preview routes */}
                {ThreadsPreviewPage ? (
                  <Route
                    path="/preview/threads"
                    element={
                      <RouteFallback>
                        <DashboardShell>
                          <ThreadsPreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {ThreadViewPreviewPage ? (
                  <Route
                    path="/preview/threads/:id"
                    element={
                      <RouteFallback>
                        <DashboardShell>
                          <ThreadViewPreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {/* Plan 291 M8-B — relationship profile page preview */}
                {RelationshipProfilePreviewPage ? (
                  <Route
                    path="/preview/relationship"
                    element={
                      <RouteFallback>
                        <DashboardShell>
                          <RelationshipProfilePreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {/* Plan 291 live-QA follow-up (item B) — Your Team index preview */}
                {TeamPreviewPage ? (
                  <Route
                    path="/preview/team"
                    element={
                      <RouteFallback>
                        <DashboardShell activeView="team">
                          <TeamPreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {/* Plan 287 M3 — Feed inbox + Settings notifications preview routes */}
                {FeedPreviewPage ? (
                  <Route
                    path="/preview/feed"
                    element={
                      <RouteFallback>
                        <DashboardShell activeView="feed">
                          <FeedPreviewPage />
                        </DashboardShell>
                      </RouteFallback>
                    }
                  />
                ) : null}
                {SettingsNotificationsPreviewPage ? (
                  <Route
                    path="/preview/settings-notifications"
                    element={
                      <RouteFallback>
                        <SettingsNotificationsPreviewPage />
                      </RouteFallback>
                    }
                  />
                ) : null}
              </>
            ) : null}
            {ADVISOR_REDIRECTS.map(({ from, to }) => (
              <Route
                key={from}
                path={from}
                element={<Navigate replace to={to} />}
              />
            ))}
            {/* Admin surface — gated by Clerk auth, RequireAdmin (role),
                and backend RequireRole(model.RoleAdmin) on every API
                call. Path-only host (app.usesill.com/admin); not a
                separate domain in MVP. */}
            <Route
              path="/admin/*"
              element={
                <Show when="signed-in" fallback={<RedirectToSignIn />}>
                  <RouteFallback>
                    <AdminSurface />
                  </RouteFallback>
                </Show>
              }
            />
            <Route path="*" element={<Navigate replace to="/onboarding" />} />
          </SentryRoutes>
        </DashboardSidebarTransitionProvider>
      </SupportProvider>
    </>
  );
}

export function RouteFallback({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<WorkspaceLoading />}>{children}</Suspense>;
}

export function WorkspaceLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="space-y-2 text-center">
        <div className="mx-auto h-8 w-8 rounded-full border-2 border-primary/25 border-t-primary motion-safe:animate-spin motion-reduce:animate-none" />
        <p className="text-sm text-muted-foreground">Loading workspace…</p>
      </div>
    </div>
  );
}
